/* eslint-disable */
// @ts-nocheck

import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useMutation, useQuery } from '@tanstack/react-query'

import {
  deleteContainerDocuments,
  getContainerDocuments,
  patchContainerDocuments,
  postContainerDocuments,
} from '@/api/containersAPI'
import { getFileDocuments } from '@/api/filesAPI'
import { deleteMedia, postMedia } from '@/api/mediaAPI'
import { getErrorMessage, isNetworkError } from '@/utils/errorHandlers'
import {
  ButtonPrimary,
  InputForm,
  Modal,
  TableContextProvider,
  TextAreaForm,
  useModalWizardStepsContext,
} from '@/components/new'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import UploadFile from '@/components/new/UploadFile'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { IDocumentTable } from '@/containers/logistic/expedients/wizzardExpedient/tabs/document/interfaces'
import useContainerWizzardSteps from '@/state/useContainerWizzardSteps'
import useDocumentIds from '@/state/useDocumentIds'

import { IContainer, IDocument } from '../../../interfaces/interfaces'
import { columns } from './columns'
import { DataTable } from './DataTable'
import DirectDownload from './DirectDownload'

export default function DocumentsContTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()

  const { useFormWizzard } = context
  const { setValue, control, watch } = useFormWizzard
  const data = useContainerWizzardSteps((state) => state.data)
  const updateData = useContainerWizzardSteps((state) => state.updateData)
  const [showUploadFile, setShowUploadFile] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [clearUploadTrigger, setClearUploadTrigger] = useState(false)
  const [dataTable, setDataTable] = useState<IDocument[]>([])
  const [selectedDocument, setSelectDocument] = useState({
    name: '',
    observations: '',
  })
  const [showDeleteDocument, setShowDeleteDocument] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<number>()

  // Query para obtener documentos en modo edición
  const { data: containerDocuments, refetch: refetchDocuments } = useQuery({
    queryKey: ['container-documents', editContainer?.id],
    queryFn: () => getContainerDocuments({ id: Number(editContainer!.id) }),
    enabled: !!isEditing && !!editContainer?.id,
  })

  // Inicializar dataTable según el modo
  useEffect(() => {
    if (isEditing && containerDocuments) {
      // En modo edición, usar documentos del backend
      setDataTable(
        containerDocuments.map((item: any) => ({
          id: item.id,
          name: item.name,
          path: item.path,
          observations: item.observations,
          actions: {
            remove: true,
            download: true,
          },
        }))
      )
    } else if (!isEditing) {
      // En modo creación, usar documentos del estado local
      setDataTable(
        data.documents.map((item, index) => ({
          id: index,
          name: item.name || `Documento ${index + 1}`,
          path: item.path,
          observations: item.observations,
          actions: {
            remove: true,
            download: true,
          },
        }))
      )
    }
  }, [isEditing, containerDocuments, data.documents])

  useEffect(() => {
    setSelectDocument({
      ...selectedDocument,
      name: watch('documentName') || '',
      observations: watch('documentObservations') || '',
    })
  }, [watch('documentName'), watch('documentObservations')])

  const sendMedia = useMutation({
    mutationFn: postMedia,
    onSuccess: (key) => {
      setIsSending(false)
      setShowUploadFile(false)

      // Limpiar el UploadFile después del éxito
      setClearUploadTrigger(true)
      setTimeout(() => setClearUploadTrigger(false), 0)

      const newDocument = {
        name: selectedDocument.name,
        path: key,
        observations: selectedDocument.observations,
        actions: {
          remove: true,
          download: true,
        },
      }

      if (isEditing && editContainer) {
        // En modo edición, crear documento directamente en el backend
        createContainerDocument.mutate({
          id: Number(editContainer.id),
          params: {
            name: selectedDocument.name,
            path: key,
            observations: selectedDocument.observations,
          },
        })
      } else {
        // En modo creación, guardar en estado local
        setSelectDocument({
          name: '',
          observations: '',
        })
        setValue('documentObservations', '')
        setValue('documentName', '')
        toast.success('Documento subido con éxito')
        setDataTable((prev) => [
          ...prev,
          {
            ...newDocument,
            id: prev.length,
          },
        ])
        // Guardar también el nombre en el estado local para que no se pierda
        updateData({
          ...data,
          documents: [
            ...data.documents,
            {
              name: selectedDocument.name,
              path: key,
              observations: selectedDocument.observations,
            },
          ],
        })
      }
    },
    onError: (error) => {
      console.error('Error uploading media:', error)
      setIsSending(false)
      setClearUploadTrigger(true)
      setTimeout(() => setClearUploadTrigger(false), 0)

      // Detect network errors and notify user when connection returns
      if (isNetworkError(error)) {
        toast.warn('Sin conexión. No se pudo subir.', { autoClose: 4000 })
        const onBack = () => {
          toast.info('Conexión restablecida.', { autoClose: 4000 })
          window.removeEventListener('online', onBack)
        }
        try {
          window.addEventListener('online', onBack)
        } catch (e) {
          // ignore in non-browser env
        }
        return
      }

      const errorMessage = getErrorMessage(error)
      toast.error(
        errorMessage || 'Ocurrió un error intentando subir el archivo'
      )
    },
  })

  // Mutaciones para documentos de contenedor
  const createContainerDocument = useMutation({
    mutationFn: postContainerDocuments,
    onSuccess: () => {
      toast.success('Documento creado con éxito')
      refetchDocuments()
      setSelectDocument({
        name: '',
        observations: '',
      })
      setValue('documentObservations', '')
      setValue('documentName', '')
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al crear el documento')
    },
  })

  const deleteContainerDocument = useMutation({
    mutationFn: deleteContainerDocuments,
    onSuccess: () => {
      toast.success('Documento eliminado con éxito')
      refetchDocuments()
      setShowDeleteDocument(false)
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al eliminar el documento')
    },
  })

  // Mutation para eliminar archivo del media
  const deleteMediaMutation = useMutation({
    mutationFn: deleteMedia,
    onSuccess: () => {
      // Archivo eliminado del media exitosamente
    },
    onError: (error) => {
      // DEBUG: No se pudo eliminar el archivo del media (esto no afecta la eliminación del documento): ${JSON.stringify(error)}
      // No mostrar toast de error para no confundir al usuario
    },
  })

  const updateContainerDocument = useMutation({
    mutationFn: patchContainerDocuments,
    onSuccess: () => {
      toast.success('Documento actualizado con éxito')
      refetchDocuments()
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al actualizar el documento')
    },
  })

  // Función para exponer el guardado a la ventana (para el botón de guardar)
  useEffect(() => {
    if (isEditing) {
      // @ts-ignore - Adding custom property to window for parent access
      window.handleSaveDocuments = () => {
        // En modo edición, no hay nada específico que guardar aquí
        // Los documentos se guardan individualmente al subirlos
        toast.success('Documentos guardados')
        // No cerrar modal automáticamente en modo edición
      }
      // @ts-ignore - Expose loading state
      window.isSavingDocuments = false
    }
  }, [isEditing])

  return (
    <div className={'h-[60dvh]'}>
      <TableContextProvider
        data={dataTable}
        isLoading={false}
        leftButtons={[
          {
            type: 'text',
            label: 'Listado de documentos',
          },
        ]}
        rightButtons={[
          {
            type: 'button',
            icon: <PlusIcon className="h-5 w-5 text-black" strokeWidth={2} />,
            label: 'Adjuntar documento',
            onClick: () => setShowUploadFile(true),
          },
        ]}
      >
        <DataTable
          columns={[
            ...columns,
            {
              accessorKey: 'actions',
              header: 'Acciones',
              cell: ({ row }) => {
                return (
                  <div className={'flex justify-center'}>
                    <DirectDownload path={row.original.path} />
                    <div
                      className={`p-2`}
                      onClick={() => {
                        setShowDeleteDocument(true)
                        setDocumentToDelete(row.original.id)
                      }}
                      role={'button'}
                    >
                      <TrashIcon className={`h-5 w-5 text-red-700`} />
                    </div>
                  </div>
                )
              },
            },
          ]}
          storageKey={'DocumentsCostTab'}
        />
      </TableContextProvider>

      {showUploadFile && (
        <Modal
          isOpen={!!showUploadFile}
          onClose={() => {
            setShowUploadFile(false)
            setSelectDocument({
              name: '',
              observations: '',
            })
            setValue('documentObservations', '')
            setValue('documentName', '')
            setClearUploadTrigger(true)
            // Reset clearTrigger después de un tick
            setTimeout(() => setClearUploadTrigger(false), 0)
          }}
          showDialogAlert={false}
          size={'l'}
          subtitle=" "
          title="Adjuntar documento"
        >
          <div className="max-h-[75vh] overflow-y-auto">
            <div className="mx-6 space-y-4" style={{ marginBottom: '-1rem' }}>
              <InputForm
                control={control}
                disabled={isSending}
                label={'Nombre del documento'}
                name={'documentName'}
                placeholder={'Ingrese el nombre del documento'}
              />
              <TextAreaForm
                control={control}
                disabled={isSending}
                label={'Observaciones'}
                name={'documentObservations'}
                placeholder={'Observaciones'}
              />
            </div>
            <UploadFile
              acceptedFileTypes={['pdf', 'images', 'word', 'excel']}
              clearTrigger={clearUploadTrigger}
              loading={isSending}
              onClear={() => {
                // Callback opcional para cuando se limpia el archivo
              }}
              onUpload={(file) => {
                const documentName = watch('documentName')
                console.log('Document name:', documentName)
                console.log('File:', file)
                console.log('Selected document:', selectedDocument)

                if (!documentName || documentName.trim() === '') {
                  toast.error('Por favor ingrese un nombre para el documento')
                  return
                }

                // If offline, notify user and register one-time online listener (no queue)
                if (typeof navigator !== 'undefined' && !navigator.onLine) {
                  toast.warn('Sin conexión. No se pudo subir.', {
                    autoClose: 4000,
                  })
                  const onBack = () => {
                    toast.info('Conexión restablecida.', { autoClose: 4000 })
                    window.removeEventListener('online', onBack)
                  }
                  if (typeof window !== 'undefined') {
                    window.addEventListener('online', onBack)
                  }
                  setIsSending(false)
                  return
                }

                // Obtener la extensión del archivo
                const fileExtension =
                  file.name.split('.').pop()?.toLowerCase() || 'png'
                console.log('File extension:', fileExtension)

                setIsSending(true)
                setSelectDocument({
                  name: documentName,
                  observations: selectedDocument.observations,
                })

                const mediaParams = {
                  params: {
                    documentType: 'IMAGE',
                    size: Math.ceil(file.size / (1024 * 1024)) || 1, // Tamaño en MB
                    ext: fileExtension,
                    isPrivate: true,
                    name: documentName,
                  },
                  file,
                }

                console.log('Sending media with params:', mediaParams)
                sendMedia.mutate(mediaParams)
              }}
            />
          </div>
        </Modal>
      )}
      <AlertDialog open={showDeleteDocument}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Si elimina ahora, perderá todos los datos asociados a este
              documento. ¿Está seguro de que desea eliminar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDocument(false)}>
              Cancelar
            </AlertDialogCancel>
            <Button
              disabled={deleteContainerDocument.isPending}
              onClick={() => {
                if (isEditing) {
                  // Obtener el documento a eliminar para obtener su path
                  const docToDelete = dataTable.find(
                    (item) => item.id === documentToDelete
                  )

                  // Si el documento tiene un path (archivo subido), eliminarlo del media primero
                  if (docToDelete?.path && docToDelete.path.trim() !== '') {
                    deleteMediaMutation.mutate(docToDelete.path)
                  }

                  deleteContainerDocument.mutate(documentToDelete as number)
                } else {
                  // Obtener el documento a eliminar para obtener su path en modo creación
                  const docToDelete = dataTable.find(
                    (item) => item.id === documentToDelete
                  )

                  // Si el documento tiene un path (archivo subido), eliminarlo del media primero
                  if (docToDelete?.path && docToDelete.path.trim() !== '') {
                    deleteMediaMutation.mutate(docToDelete.path)
                  }

                  setDataTable(
                    dataTable.filter(
                      (item) => item.id !== (documentToDelete as number)
                    )
                  )
                  updateData({
                    ...data,
                    documents: data.documents.filter(
                      (_, index) => index !== (documentToDelete as number)
                    ),
                  })
                  setShowDeleteDocument(false)
                  toast.success('El documento se ha eliminado con éxito')
                }
              }}
            >
              {deleteContainerDocument.isPending
                ? 'Eliminando...'
                : 'Sí, eliminar'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
